import PropTypes from 'prop-types';
import React from 'react';
import {BASE_DIALOG_WIDTH} from '../constants';
import Button from './Button';
import color from '../util/color';
import i18n from '@cdo/locale';

/**
 * BaseDialog
 * A generic modal dialog that has an x-close in the upper right, and a
 * semi-transparent backdrop. Can be closed by clicking the x, clicking the
 * backdrop, or pressing esc.
 *
 * To use a styled version of this component that follows our style guide,
 * use StylizedBaseDialog.jsx.
 */
export default class BaseDialog extends React.Component {
  static propTypes = {
    isOpen: PropTypes.bool,
    handleClose: PropTypes.func,
    uncloseable: PropTypes.bool,
    hideCloseButton: PropTypes.bool,
    handleKeyDown: PropTypes.func,
    // For use in react-storybook; allows rendering dialog inline in story tables.
    hideBackdrop: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    useUpdatedStyles: PropTypes.bool,
    noModalStyles: PropTypes.bool,
    children: PropTypes.node,
    fixedWidth: PropTypes.number,
    fixedHeight: PropTypes.number,
    useFlexbox: PropTypes.bool,
    bodyId: PropTypes.string,
    bodyClassName: PropTypes.string,
    style: PropTypes.object,
    soundPlayer: PropTypes.object,
    overflow: PropTypes.string,
    // Temporary prop until AnimationPickerBody is redesigned
    backdropStyle: PropTypes.object,
  };

  componentDidMount() {
    this.focusDialog();
  }

  componentDidUpdate() {
    this.focusDialog();
  }

  handleKeyDown = event => {
    if (event.key === 'Escape') {
      this.closeDialog();
    }
    this.props.handleKeyDown && this.props.handleKeyDown(event);
  };

  closeDialog = () => {
    if (!this.props.uncloseable && this.props.handleClose) {
      this.props.handleClose();
      if (this.props.soundPlayer) {
        this.props.soundPlayer.stopAllAudio();
      }
    }
  };

  /** @returns {Array.<Element>} */
  getTabbableElements() {
    return [].slice.call(this.refs.dialog.querySelectorAll('a,button,input'));
  }

  focusDialog() {
    // Don't steal focus if the active element is already a descendant of the
    // dialog - prevents focus loss on updates of open BaseDialog components.
    const descendantIsActive =
      document.activeElement &&
      this.refs.dialog &&
      this.refs.dialog.contains(document.activeElement);
    if (this.props.isOpen && !descendantIsActive) {
      this.refs.dialog.focus();
    }
  }

  render() {
    if (!this.props.isOpen && !this.props.hideBackdrop) {
      return <div />;
    }

    let bodyStyle, modalBodyStyle, xCloseStyle;
    if (this.props.fullWidth) {
      bodyStyle = {
        ...bodyStyle,
        width: '90%',
        marginLeft: '-45%',
      };
    }
    if (this.props.fullHeight) {
      bodyStyle = {
        ...bodyStyle,
        height: '80%',
      };
      modalBodyStyle = {
        ...modalBodyStyle,
        boxSizing: 'border-box',
      };
    }

    let wrapperClassNames = '';
    let modalClassNames = 'modal';
    let modalBodyClassNames = 'modal-body';
    let modalBackdropClassNames = 'modal-backdrop';
    const overflowX = this.props.overflow || 'hidden';
    const overflowY =
      this.props.overflow ||
      (this.props.fixedHeight || this.props.fullHeight ? 'hidden' : 'auto');

    if (this.props.useUpdatedStyles) {
      wrapperClassNames = 'dashboard-styles';
      modalBodyClassNames = '';
      modalBodyStyle = {
        background: '#fff',
        height: this.props.fixedHeight,
        maxHeight: !this.props.fixedHeight && '80vh',
        overflowX: overflowX,
        overflowY: overflowY,
        borderRadius: 4,
      };
      if (this.props.useFlexbox) {
        modalBodyStyle = {
          ...modalBodyStyle,
          display: 'flex',
          flexDirection: 'column',
        };
      }
      bodyStyle = {
        ...bodyStyle,
        width: this.props.fixedWidth || BASE_DIALOG_WIDTH,
        marginLeft: -this.props.fixedWidth / 2 || -350,
      };
    } else if (this.props.noModalStyles) {
      modalClassNames = '';
      modalBodyClassNames = '';
    }

    bodyStyle = {
      ...bodyStyle,
      ...(this.props.hideBackdrop && {
        position: 'initial',
        marginLeft: 0,
      }),
      ...this.props.style,
    };
    xCloseStyle = {
      position: 'absolute',
      top: 0,
      right: 0,
      padding: 0,
      color: color.neutral_dark30,
      cursor: 'pointer',
      fontSize: 24,
      border: 'none',
    };

    modalBodyClassNames = [modalBodyClassNames, this.props.bodyClassName]
      .filter(className => !!className)
      .join(' ');

    let body = (
      <div
        style={bodyStyle}
        tabIndex="-1"
        className={modalClassNames}
        ref="dialog"
        onKeyDown={this.handleKeyDown}
      >
        <div
          style={modalBodyStyle}
          id={this.props.bodyId}
          className={modalBodyClassNames}
        >
          {!this.props.uncloseable && !this.props.hideCloseButton && (
            <Button
              id="x-close"
              onClick={this.closeDialog}
              icon="fa-solid fa-xmark"
              style={xCloseStyle}
              color="white"
              aria-label={i18n.closeDialog()}
            />
          )}
          {this.props.children}
        </div>
      </div>
    );

    if (this.props.hideBackdrop) {
      return body;
    }

    return (
      <div className={wrapperClassNames}>
        <div
          className={modalBackdropClassNames}
          style={this.props.backdropStyle}
          onClick={this.closeDialog}
        />
        {body}
      </div>
    );
  }
}
